import * as amplitude from '@amplitude/analytics-browser';
import { useQueryClient } from '@tanstack/react-query';

import {
  CLASS_NAME,
  QUERY_KEYS,
} from 'hb-react/frontoffice/apps/Forum/constants';
import { useForumContext } from 'hb-react/frontoffice/apps/Forum/contexts/ForumContext';
import { useFollowDiscussion } from 'hb-react/frontoffice/apps/Forum/hooks/queries/useFollowDiscussion';
import type { Discussion } from 'hb-react/frontoffice/apps/Forum/types';
import { ITEMS_QUERY_KEYS } from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils/queryUtils';
import Reactions from 'hb-react/frontoffice/components/Reactions';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import toggleLike from 'hb-react/shared/components/DesignSystem/Reactions/utils/toggleLike';
import useNavigatorShare from 'hb-react/shared/hooks/useNavigatorShare';

interface DiscussionReactionsProps {
  discussion: Discussion;
}

const DiscussionReactions = ({ discussion }: DiscussionReactionsProps) => {
  const { configUrls, appPageConfig } = useForumContext();
  const { topicId } = useTopicContext();
  const queryClient = useQueryClient();
  const {
    id,
    likes,
    title,
    comments,
    is_following: isFollowing,
    user,
  } = discussion;
  const { mutate, isPending } = useFollowDiscussion({
    discussionId: id,
    follow: true,
  });
  const sharedResourceUrl = `${
    window.location.origin
  }${configUrls.discussionUrl(id)}`;
  const { currentUser } = useDefaultAppContexts();

  const { handleShare, shareUrl } = useNavigatorShare({
    data: {
      text: title,
      url: sharedResourceUrl,
    },
    onShare: () => {
      amplitude.track('Forum|DiscussionShared');
    },
  });

  const followOnNewComment = () => {
    if (!isFollowing && !isPending && currentUser?.id !== user.id) {
      mutate();
    }
  };

  const onClickLike = async (data) => {
    const likeData = await toggleLike(data);

    queryClient.invalidateQueries({
      queryKey: [ITEMS_QUERY_KEYS.forum.items, topicId],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.discussion, id, topicId],
    });

    return likeData;
  };

  const onComment = () => {
    queryClient.invalidateQueries({
      queryKey: [ITEMS_QUERY_KEYS.forum.items, topicId],
    });
  };

  const enableVotes = !!appPageConfig?.config?.enable_votes;

  return (
    <Reactions
      displayCommentsByDefault
      options={{
        votes: {
          enableVotes,
          onSuccess: (state) =>
            amplitude.logEvent('Forums|UpdownVotes', {
              state,
              group_id: topicId,
            }),
        },
      }}
      handleShareButton={handleShare}
      id={id}
      key={id}
      // @ts-expect-error untyped component
      initialLikes={likes}
      initialTotalComments={comments.total}
      onNewCommentCallback={followOnNewComment}
      onTogglePostLike={onClickLike}
      railsType={CLASS_NAME}
      shareUrl={shareUrl}
      type={CLASS_NAME}
      onCommentCallback={onComment}
    />
  );
};

export default DiscussionReactions;
